import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { contentApi } from "./contentApi";
import { AdminListPager, AdminListToolbar } from "@/components/admin/AdminListToolbar";
import { useAdminListQuery } from "@/components/admin/useAdminListQuery";

const emptyContext = () => ({
  level: "1",
  maturity: "Developing",
  gaps: "",
  brief: "",
});

export default function PromptEditor({
  onMessage,
  onDirtyChange,
  onBusyChange,
}) {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [body, setBody] = useState("");
  const [savedBody, setSavedBody] = useState("");
  const [dirty, setDirty] = useState(false);
  const [bodyLoading, setBodyLoading] = useState(false);
  const [bodyUnavailable, setBodyUnavailable] = useState(true);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [refreshError, setRefreshError] = useState("");
  const [reconcilePending, setReconcilePending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewContext, setPreviewContext] = useState(emptyContext);
  const [confirm, setConfirm] = useState(null);
  const [pendingSelection, setPendingSelection] = useState(null);
  const generation = useRef(0);
  const selectedId = useRef(null);
  const busyRef = useRef(false);
  const abortRef = useRef(null);
  const previewGeneration = useRef(0);
  const previewAbortRef = useRef(null);
  const previewOutputRef = useRef(null);
  const [total, setTotal] = useState(0);
  const { query, update, reset } = useAdminListQuery();

  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  useEffect(
    () => onBusyChange(busy || reconcilePending),
    [busy, reconcilePending, onBusyChange],
  );
  useEffect(() => {
    if (preview) previewOutputRef.current?.focus();
  }, [preview]);

  function promptListPath() {
    const params = new URLSearchParams({ paged: "true", limit: "100" });
    for (const key of ["q", "status", "offset"]) if (query[key]) params.set(key, query[key]);
    return `/admin/content/prompts?${params}`;
  }

  async function loadList({ reconcile = false, refreshErrorPrefix = "" } = {}) {
    const current = ++generation.current;
    setLoading(true);
    if (reconcile) setReconcilePending(true);
    else setReconcilePending(false);
    setError("");
    try {
      const response = await contentApi(promptListPath());
      const result = response.items || [];
      if (current !== generation.current) return;
      setRows(result);
      setTotal(Number(response.total) || 0);
      // The list is complete before selecting a version starts its own read
      // generation. Otherwise that selection skips this request's finally.
      setLoading(false);
      const currentId = selectedId.current;
      const matching = result.find((row) => row.id === currentId);
      if (matching) {
        setSelected(matching);
        if (!dirty) {
          loadDetails(matching);
        }
      } else if ((!currentId || (currentId && !matching)) && !dirty) {
        const next = result[0] || null;
        setSelected(next);
        loadDetails(next);
      }
      setRefreshError("");
      setReconcilePending(false);
      return true;
    } catch (e) {
      if (current === generation.current) {
        setRefreshError(`${refreshErrorPrefix}${e.message}`);
        setReconcilePending(reconcile);
      }
      return false;
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }

  useEffect(() => {
    loadList();
    return () => {
      generation.current += 1;
      abortRef.current?.abort();
      previewGeneration.current += 1;
      previewAbortRef.current?.abort();
    };
  }, [query.q, query.status, query.offset]);

  async function loadDetails(row) {
    const current = ++generation.current;
    selectedId.current = row?.id || null;
    abortRef.current?.abort();
    if (!row) {
      setBody("");
      setSavedBody("");
      setBodyLoading(false);
      setBodyUnavailable(false);
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setBody("");
    setSavedBody("");
    setBodyLoading(true);
    setBodyUnavailable(true);
    setError("");
    try {
      const detail = await contentApi(`/admin/content/prompts/${row.id}`, {
        signal: controller.signal,
      });
      if (current !== generation.current || selectedId.current !== row.id)
        return;
      setBodyUnavailable(false);
      setBody(detail.body);
      setSavedBody(detail.body);
      setDirty(false);
    } catch (e) {
      if (e.name !== "AbortError" && current === generation.current)
        setError(e.message);
    } finally {
      if (current === generation.current) setBodyLoading(false);
    }
  }

  function selectVersion(row, ask = true) {
    if (busyRef.current || row?.id === selectedId.current) return;
    if (ask && dirty) {
      setPendingSelection(row);
      return;
    }
    setSelected(row);
    setDirty(false);
    loadDetails(row);
  }

  function discardAndSelect() {
    const row = pendingSelection;
    setPendingSelection(null);
    setDirty(false);
    setSelected(row);
    loadDetails(row);
  }

  function updateBody(value) {
    setBody(value);
    setDirty(value !== savedBody);
  }

  async function save() {
    if (
      !selected ||
      selected.status !== "draft" ||
      busyRef.current ||
      bodyLoading ||
      bodyUnavailable
    )
      return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    const id = selected.id;
    const current = generation.current;
    const nextBody = body;
    try {
      const row = await contentApi(`/admin/content/prompts/${id}`, {
        method: "PATCH",
        body: { body: nextBody },
      });
      if (current !== generation.current || selectedId.current !== id) return;
      setRows((old) => old.map((item) => (item.id === row.id ? row : item)));
      setSelected(row);
      setSavedBody(nextBody);
      setDirty(false);
      onMessage("Draft saved");
      await reconcileList(id);
    } catch (e) {
      if (current === generation.current) setError(e.message);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  async function reconcileList(id, refreshErrorPrefix = "Saved, but refresh failed: ") {
    if (selectedId.current !== id) return false;
    return loadList({ reconcile: true, refreshErrorPrefix });
  }

  function requestNewVersion() {
    if (dirty) {
      setConfirm({ action: "new-version", discard: true });
      return;
    }
    createVersion();
  }

  async function createVersion() {
    if (
      !selected ||
      busyRef.current ||
      bodyUnavailable ||
      bodyLoading ||
      reconcilePending
    )
      return;
    busyRef.current = true;
    setBusy(true);
    setError("");
    try {
      const row = await contentApi(
        `/admin/content/prompts/${selected.id}/new-version`,
        { method: "POST", body: {} },
      );
      setSelected(row);
      setDirty(false);
      await loadDetails(row);
      await reconcileList(row.id, "New version created, but refresh failed: ");
      onMessage("New draft version created");
    } catch (e) {
      setError(e.message);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  function requestPublish() {
    if (dirty) {
      setConfirm({ action: "publish", discard: true });
      return;
    }
    setConfirm({ action: "publish" });
  }

  async function publish({ discarded = false } = {}) {
    if (
      !selected ||
      selected.status !== "draft" ||
      busyRef.current ||
      bodyUnavailable ||
      bodyLoading ||
      reconcilePending
    )
      return;
    const id = selected.id;
    busyRef.current = true;
    setBusy(true);
    setError("");
    try {
      const row = await contentApi(`/admin/content/prompts/${id}/publish`, {
        method: "POST",
        body: {},
      });
      setRows((old) =>
        old.map((item) =>
          item.key === row.key && item.status === "published"
            ? { ...item, status: "retired" }
            : item.id === row.id
              ? row
              : item,
        ),
      );
      setSelected(row);
      const persistedBody = row.body ?? (discarded ? savedBody : body);
      setBody(persistedBody);
      setSavedBody(persistedBody);
      setDirty(false);
      onMessage("Prompt published");
      await reconcileList(id);
    } catch (e) {
      setError(e.message);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  async function removeDraft() {
    if (
      !selected ||
      selected.status !== "draft" ||
      busyRef.current ||
      bodyUnavailable ||
      bodyLoading ||
      reconcilePending
    )
      return;
    const id = selected.id;
    busyRef.current = true;
    setBusy(true);
    setConfirm(null);
    setError("");
    try {
      await contentApi(`/admin/content/prompts/${id}`, { method: "DELETE" });
      const next = rows.find((item) => item.id !== id);
      setRows((old) => old.filter((item) => item.id !== id));
      setSelected(next || null);
      await loadDetails(next || null);
      await loadList({ reconcile: true });
      onMessage("Draft deleted");
    } catch (e) {
      setError(e.message);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  }

  async function doPreview() {
    if (
      !selected ||
      busyRef.current ||
      bodyLoading ||
      bodyUnavailable ||
      previewLoading
    )
      return;
    previewAbortRef.current?.abort();
    const request = ++previewGeneration.current;
    const controller = new AbortController();
    previewAbortRef.current = controller;
    busyRef.current = true;
    setBusy(true);
    setError("");
    setPreviewError("");
    setPreview(null);
    setPreviewLoading(true);
    try {
      const result = await contentApi("/admin/content/prompts/preview", {
        method: "POST",
        body: { body, context: previewContext },
        signal: controller.signal,
      });
      if (request !== previewGeneration.current || !previewOpen) return;
      setPreview(result);
    } catch (e) {
      if (e.name !== "AbortError" && request === previewGeneration.current)
        setPreviewError(e.message);
    } finally {
      if (request === previewGeneration.current) {
        setPreviewLoading(false);
        busyRef.current = false;
        setBusy(false);
      }
    }
  }

  function openPreview() {
    previewGeneration.current += 1;
    previewAbortRef.current?.abort();
    previewAbortRef.current = null;
    setPreview(null);
    setPreviewError("");
    setPreviewLoading(false);
    setPreviewOpen(true);
  }

  function closePreview() {
    previewGeneration.current += 1;
    previewAbortRef.current?.abort();
    previewAbortRef.current = null;
    setPreviewLoading(false);
    setPreview(null);
    setPreviewError("");
    busyRef.current = false;
    setBusy(false);
    setPreviewOpen(false);
  }

  function confirmAction() {
    const action = confirm?.action;
    const discard = Boolean(confirm?.discard && dirty);
    setConfirm(null);
    if (discard) {
      setBody(savedBody);
      setDirty(false);
    }
    if (action === "new-version") createVersion();
    else if (action === "publish") publish({ discarded: discard });
    else if (action === "delete") removeDraft();
  }

  function updateContext(key, value) {
    previewGeneration.current += 1;
    previewAbortRef.current?.abort();
    previewAbortRef.current = null;
    setPreview(null);
    setPreviewError("");
    setPreviewContext((old) => ({ ...old, [key]: value }));
  }

  return (
    <section
      className="content-editor-section"
      aria-labelledby="prompt-editor-title"
    >
      <div className="content-section-heading">
        <div>
          <h2 id="prompt-editor-title">Prompt versions</h2>
          <p>
            Drafts can be edited and previewed. Published bodies remain
            read-only.
          </p>
        </div>
        <button
          type="button"
          className="content-button secondary"
          onClick={() => loadList({ reconcile: reconcilePending })}
          disabled={busy || loading || reconcilePending}
        >
          Refresh
        </button>
      </div>
      <AdminListToolbar search={query.q || ""} searchLabel="Search prompt key" total={total} loading={loading} onSearch={(q) => update({ q })} onReset={reset}>
        <label className="text-sm font-medium">Status<select aria-label="Filter prompt status" value={query.status || "all"} onChange={(event) => update({ status: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All statuses</option><option value="draft">Draft</option><option value="published">Published</option><option value="retired">Retired</option></select></label>
      </AdminListToolbar>
      {error && (
        <p className="content-error" role="alert">
          {error}
          {bodyUnavailable && (
            <button
              type="button"
              className="content-button secondary"
              onClick={() => loadDetails(selected)}
              disabled={busy || bodyLoading}
            >
              Retry prompt body
            </button>
          )}
        </p>
      )}
      {refreshError && (
        <p className="content-error" role="alert">
          {refreshError}{" "}
          <button
            type="button"
            className="content-button secondary"
            onClick={() => loadList({ reconcile: true })}
            disabled={busy || loading}
          >
            Retry refresh
          </button>
        </p>
      )}
      <div className="content-prompt-layout">
        <div className="content-card">
          <h3>Versions</h3>
          <div className="content-row-list">
            {rows.map((row) => (
              <button
                type="button"
                className={`content-version ${selected?.id === row.id ? "selected" : ""}`}
                key={row.id}
                onClick={() => selectVersion(row)}
                disabled={busy || reconcilePending}
              >
                <span>
                  <strong>{row.key}</strong>
                  <small>Version {row.version}</small>
                </span>
                <span className={`content-status ${row.status}`}>
                  {row.status}
                </span>
              </button>
            ))}
        </div>
        <AdminListPager offset={Number(query.offset || 0)} limit={100} total={total} disabled={loading || Boolean(busy)} onPage={(offset) => update({ offset }, { resetOffset: false })} />
      </div>
        {selected && (
          <div className="content-card content-form">
            <div className="content-section-heading">
              <div>
                <h3>
                  {selected.key} · version {selected.version}
                </h3>
                <span className={`content-status ${selected.status}`}>
                  {selected.status}
                </span>
              </div>
              <button
                type="button"
                className="content-button secondary"
                onClick={requestNewVersion}
                disabled={
                  busy || bodyLoading || bodyUnavailable || reconcilePending
                }
              >
                New version
              </button>
            </div>
            <fieldset
              disabled={
                busy || bodyLoading || bodyUnavailable || reconcilePending
              }
            >
              <label>
                Prompt body
                <textarea
                  className="content-prompt-textarea"
                  maxLength={50000}
                  readOnly={selected.status !== "draft"}
                  value={body}
                  onChange={(e) => updateBody(e.target.value)}
                />
              </label>
            </fieldset>
            <p className="content-muted">
              {body.length} / 50,000 characters · placeholders: {"{{level}}"},{" "}
              {"{{maturity}}"}, {"{{gaps}}"}, {"{{brief}}"}
            </p>
            <div className="content-actions">
              <button
                type="button"
                className="content-button secondary"
                onClick={openPreview}
                disabled={
                  busy || bodyLoading || bodyUnavailable || reconcilePending
                }
              >
                Preview
              </button>
              {selected.status === "draft" && (
                <>
                  <button
                    type="button"
                    className="content-button primary"
                    onClick={save}
                    disabled={
                      busy || bodyLoading || bodyUnavailable || reconcilePending
                    }
                  >
                    Save draft
                  </button>
                  <button
                    type="button"
                    className="content-button primary"
                    onClick={requestPublish}
                    disabled={
                      busy || bodyLoading || bodyUnavailable || reconcilePending
                    }
                  >
                    Publish
                  </button>
                  <button
                    type="button"
                    className="content-button danger"
                    onClick={() => setConfirm({ action: "delete" })}
                    disabled={
                      busy || bodyLoading || bodyUnavailable || reconcilePending
                    }
                  >
                    Delete draft
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      <Dialog
        open={previewOpen}
        onOpenChange={(open) => (open ? setPreviewOpen(true) : closePreview())}
      >
        <DialogContent className="content-preview-dialog">
          <DialogHeader>
            <DialogTitle>Prompt preview</DialogTitle>
            <DialogDescription>
              Preview this draft with sample context. This does not save your
              changes.
            </DialogDescription>
          </DialogHeader>
          <fieldset
            className="content-form-grid content-preview-context"
            disabled={previewLoading}
          >
            <legend className="sr-only">Sample context</legend>
            {[
              ["level", "Level"],
              ["maturity", "Maturity"],
              ["gaps", "Gaps"],
              ["brief", "Organisation brief"],
            ].map(([key, label]) => (
              <label key={key}>
                {label}
                <input
                  maxLength={10000}
                  value={previewContext[key]}
                  onChange={(e) => updateContext(key, e.target.value)}
                />
              </label>
            ))}
          </fieldset>
          {previewError && (
            <p className="content-error" role="alert">
              {previewError}
            </p>
          )}
          {preview ? (
            <pre
              ref={previewOutputRef}
              className="content-preview-output"
              tabIndex={0}
              aria-label="Rendered prompt preview"
            >
              {preview.rendered}
            </pre>
          ) : (
            <p className="content-muted">
              Enter sample context, then choose Preview.
            </p>
          )}
          <DialogFooter>
            <button
              type="button"
              className="content-button primary"
              onClick={doPreview}
              disabled={
                busy ||
                bodyLoading ||
                bodyUnavailable ||
                reconcilePending ||
                previewLoading
              }
            >
              Render preview
            </button>
            <button
              type="button"
              className="content-button secondary"
              onClick={closePreview}
            >
              Close
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog
        open={Boolean(confirm)}
        onOpenChange={(open) => !open && setConfirm(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.action === "publish"
                ? confirm?.discard
                  ? "Discard changes and publish?"
                  : "Publish this prompt version?"
                : confirm?.action === "new-version"
                  ? "Discard changes and create a version?"
                  : "Delete this draft?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.action === "publish"
                ? "The saved server body will be published and your unsaved editor changes will be discarded."
                : confirm?.action === "new-version"
                  ? "The new version will copy the saved server body and discard your unsaved editor changes."
                  : "Only draft versions can be deleted."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirm(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmAction}>
              {confirm?.action === "publish"
                ? confirm?.discard
                  ? "Discard and publish"
                  : "Publish"
                : confirm?.action === "new-version"
                  ? "Discard and create version"
                  : "Delete draft"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={Boolean(pendingSelection)}
        onOpenChange={(open) => !open && setPendingSelection(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard prompt changes?</AlertDialogTitle>
            <AlertDialogDescription>
              Selecting another version will discard this unsaved body.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingSelection(null)}>
              Keep editing
            </AlertDialogCancel>
            <AlertDialogAction onClick={discardAndSelect}>
              Discard changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
